import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useThemeVariant } from "@/contexts/ThemeContext";
import { useOrg } from "@/contexts/OrgContext";
import { useAuth } from "@/contexts/AuthContext";
import { useIsThemePreviewFrame } from "@/hooks/useIsThemePreviewFrame";
import { useNavCustomPages } from "@/hooks/useNavCustomPages";
import { useCompanyInfoBrief } from "@/hooks/useCompanyInfo";
import { isAwaitingFirstResult, useTenantOrgId } from "@/hooks/useTenantOrgId";
import { Skel } from "@/components/home/TenantSkeletons";
import { TenantImage } from "@/components/home/TenantImage";
import logo from "@/assets/logo-header.webp";
import demoFallbackLogo from "@/assets/demo-fallback-logo.png";
import placeholderLogo from "@/assets/logo-placeholder.webp";
import placeholderLogoLight from "@/assets/logo-placeholder-light.webp";

const baseNavLinks = [
  { name: "Home", href: "/" },
  { name: "Services", href: "/services" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/contact" },
];

const demoNavLinks = baseNavLinks.map((link) => ({
  ...link,
  href: link.href === "/" ? "/demo" : `/demo${link.href}`,
}));

const demoHeaderLinks = [
  { name: "Polished Version", href: "/" },
  { name: "Demo Home", href: "/demo" },
  ...demoNavLinks.filter((link) => link.href !== "/demo"),
];

export function Navbar() {
  const { isMarketingSite } = useOrg();
  const orgId = useTenantOrgId();
  const { isOrgOwner, staffRole } = useAuth();
  const isPreviewFrame = useIsThemePreviewFrame();
  const isEditorFrame = useLocation().pathname.startsWith('/admin/editor-frame');
  const showAdminBar = (isOrgOwner || staffRole === 'super_admin') && !isMarketingSite && !isPreviewFrame && !isEditorFrame;
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isDemoRoute = location.pathname === "/demo" || location.pathname.startsWith("/demo/");

  const companyPendingQuery = useCompanyInfoBrief(orgId);
  const { data: customPages = [] } = useNavCustomPages(orgId);
  const { data: companyInfo = null } = companyPendingQuery;
  const companyPending = isAwaitingFirstResult(companyPendingQuery);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close the mobile menu on navigation.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Combine base links with custom pages
  const navLinks = [
    ...(isDemoRoute ? demoHeaderLinks : baseNavLinks),
    ...customPages.map(page => ({
      name: page.title,
      href: `/p/${page.slug}`,
    })),
  ];

  const bookingHref = isDemoRoute ? "/demo/booking" : "/booking";
  // Fixed height, natural width: the logo keeps the exact shape the owner cropped
  // (a cap stops a very wide one crowding the menu).
  const logoClass = "tp-nav-logo h-[64px] w-auto max-w-[220px] object-contain md:h-[72px] md:max-w-[260px]";

  const split = useThemeVariant() === "elegant-serif";
  const half = Math.ceil(navLinks.length / 2);
  const renderLink = (link: { name: string; href: string }) => (
    <Link
      key={link.href}
      to={link.href}
      aria-current={location.pathname === link.href ? "page" : undefined}
      className="tp-nav-link"
    >
      {link.name}
    </Link>
  );
  const logoLink = (
  <Link to={isDemoRoute ? "/demo" : "/"} className={cn("flex items-center gap-2", !split && "min-w-[128px] md:min-w-[144px]")}>
      {isDemoRoute ? (
        // The demo shows prospects where their own logo goes.
        <>
          <img
            src={placeholderLogo}
            alt="Your logo here"
            className={cn(logoClass, "dark:hidden")}
            width={960}
            height={480}
            loading="eager"
            decoding="sync"
            fetchPriority="high"
          />
          <img
            src={placeholderLogoLight}
            alt=""
            aria-hidden
            className={cn(logoClass, "hidden dark:block")}
            width={960}
            height={480}
            loading="lazy"
            decoding="async"
          />
        </>
      ) : companyPending ? (
        // Until we know whose site this is, hold a logo-shaped shimmer
        // rather than flashing a fallback logo.
        <span className="tp-skeleton flex h-[64px] w-[128px] items-center md:h-[72px] md:w-[144px]" aria-hidden>
          <Skel className="h-9 w-full rounded-xl" />
        </span>
      ) : companyInfo?.logo_url ? (
        <TenantImage
          src={companyInfo.logo_url}
          alt={companyInfo.company_name || 'Your business'}
          className={logoClass}
          width={480}
          height={240}
          loading="eager"
          decoding="sync"
          fetchPriority="high"
        />
      ) : isMarketingSite ? (
        <img
          src={logo}
          alt="The Polished Version"
          className={logoClass}
          width={480}
          height={240}
          loading="eager"
          decoding="sync"
          fetchPriority="high"
        />
      ) : (
        <img
          src={demoFallbackLogo}
          alt={companyInfo?.company_name || 'Your business'}
          className={logoClass}
          width={2000}
          height={1000}
          loading="eager"
          decoding="sync"
          fetchPriority="high"
        />
      )}
    </Link>
  );
  const ctaButton = (
    <Button variant="hero" size="sm" className="tp-btn tp-nav-cta group h-10 pl-5 pr-4 hover:translate-y-0" asChild>
                <Link to={bookingHref}>
                  Book my cleaning
                  <ArrowUpRight className="!size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </Button>
  );

  return (
    <nav
      data-scrolled={scrolled || isOpen ? "true" : "false"}
      className={cn(
        "tp-nav fixed left-0 right-0 z-50",
        showAdminBar ? "top-9" : "top-0",
      )}
    >
      <div className="container mx-auto px-5 md:px-6">
        <div className="tp-nav-inner relative flex h-20 items-center justify-between gap-6">
          {split ? (
            <>
              <div className="hidden flex-1 justify-end md:flex">
                <div className="tp-nav-pill flex items-center gap-0.5">{navLinks.slice(0, half).map(renderLink)}</div>
              </div>
              {logoLink}
              <div className="hidden flex-1 items-center justify-start md:flex">
                <div className="tp-nav-pill flex items-center gap-0.5">{navLinks.slice(half).map(renderLink)}</div>
              </div>
              {/* Pinned right so the masthead logo stays dead centre. */}
              <div className="absolute right-0 top-1/2 hidden -translate-y-1/2 xl:block">{ctaButton}</div>
              <span className="md:hidden" />
            </>
          ) : (
            <>
              {logoLink}
              <div className="tp-nav-pill hidden items-center gap-0.5 md:flex">{navLinks.map(renderLink)}</div>
              <div className="hidden md:block">{ctaButton}</div>
            </>
          )}

          {/* Mobile Menu Toggle */}
          <button
            className="flex h-11 w-11 items-center justify-center rounded-full border border-foreground/10 bg-card/70 text-foreground md:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={cn(
            "md:hidden overflow-hidden transition-all duration-300",
            isOpen ? "max-h-[640px] pb-5" : "max-h-0"
          )}
        >
          <div className="tp-mobile-menu flex flex-col gap-1 p-3">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsOpen(false)}
                aria-current={location.pathname === link.href ? "page" : undefined}
                className={cn(
                  "flex items-center justify-between rounded-xl px-4 py-3.5 text-base font-medium transition-colors duration-200",
                  location.pathname === link.href
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {link.name}
                <ArrowRight className="h-4 w-4 opacity-40" />
              </Link>
            ))}
            <Button variant="hero" size="lg" className="tp-btn mt-2 hover:translate-y-0" asChild>
              <Link to={bookingHref} onClick={() => setIsOpen(false)}>
                Book my cleaning
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}
